import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, GraduationCap, BookOpen, Users, Crown } from 'lucide-react'
import { courseApi, type PublicCourse } from '../api/lessonBuilderApi'
import { COURSE_LEVELS, GRADE_OPTIONS, courseGrade, type CourseGrade } from '../types/CourseDoc'
import StarRating from '../components/StarRating'

const levelLabel = (lv?: string) => COURSE_LEVELS.find((x) => x.value === lv)?.label || ''
const GRADES: CourseGrade[] = ['10', '11', '12', 'khac']
const GRADE_TITLE: Record<CourseGrade, string> = { '10': 'Lớp 10', '11': 'Lớp 11', '12': 'Lớp 12', khac: 'Khác (ôn thi · HSG · chuyên đề)' }
const GRADE_ACCENT: Record<CourseGrade, string> = {
  '10': 'from-sky-500 to-blue-600', '11': 'from-violet-500 to-indigo-600',
  '12': 'from-rose-500 to-pink-600', khac: 'from-amber-500 to-orange-600',
}

// Thẻ 1 khoá học (kiểu catalog chuyên nghiệp).
const CourseCard: React.FC<{ c: PublicCourse; accent: string; onClick: () => void }> = ({ c, accent, onClick }) => (
  <button onClick={onClick}
    className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden flex flex-col text-left hover:shadow-lg hover:border-blue-200 hover:-translate-y-0.5 transition-all">
    <div className={`h-32 bg-gradient-to-br ${accent} flex items-center justify-center relative`}>
      {c.coverImage ? <img src={c.coverImage} alt={c.title} className="w-full h-full object-cover" /> : <GraduationCap className="text-white/80" size={34} />}
      <span className={`absolute top-2 right-2 text-[10px] font-black px-2 py-0.5 rounded-full flex items-center gap-1 ${c.access === 'pro' ? 'bg-amber-400 text-amber-900' : 'bg-white/90 text-emerald-700'}`}>
        {c.access === 'pro' ? <><Crown size={11} /> PRO</> : 'Miễn phí'}
      </span>
    </div>
    <div className="p-4 flex flex-col flex-1">
      <div className="flex items-center gap-1.5 flex-wrap mb-1">
        {c.category && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-blue-50 text-blue-600">{c.category}</span>}
        {levelLabel(c.level) && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-purple-50 text-purple-600">{levelLabel(c.level)}</span>}
      </div>
      <h3 className="text-base font-bold text-gray-900 leading-snug line-clamp-2">{c.title}</h3>
      {c.instructorName && <p className="text-[11px] text-gray-500 mt-0.5">👩‍🏫 {c.instructorName}</p>}
      <p className="text-xs text-gray-400 mt-1 line-clamp-2 flex-1">{c.subtitle || '—'}</p>
      <div className="mt-2"><StarRating value={c.ratingAvg || 0} count={c.ratingCount || 0} size={14} /></div>
      <div className="flex items-center gap-4 text-[11px] text-gray-400 mt-2">
        <span className="flex items-center gap-1"><BookOpen size={13} /> {c.lessonCount} bài</span>
        <span className="flex items-center gap-1"><Users size={13} /> {c.enrolledCount} học viên</span>
      </div>
    </div>
  </button>
)

// Catalog công khai: tách theo LỚP 10 / 11 / 12 / Khác + tìm kiếm + chip nhảy nhanh.
const CatalogPage: React.FC = () => {
  const navigate = useNavigate()
  const [courses, setCourses] = useState<PublicCourse[]>([])
  const [loading, setLoading] = useState(true)
  const [q, setQ] = useState('')

  useEffect(() => {
    courseApi.publicCourses().then(setCourses).finally(() => setLoading(false))
  }, [])

  // Lọc theo từ khoá rồi gom theo lớp.
  const byGrade = useMemo(() => {
    const kw = q.trim().toLowerCase()
    const m: Record<CourseGrade, PublicCourse[]> = { '10': [], '11': [], '12': [], khac: [] }
    courses
      .filter((c) => !kw || `${c.title} ${c.subtitle || ''} ${c.category || ''} ${c.instructorName || ''}`.toLowerCase().includes(kw))
      .forEach((c) => m[courseGrade(c)].push(c))
    return m
  }, [courses, q])

  const total = GRADES.reduce((s, g) => s + byGrade[g].length, 0)
  const jump = (g: CourseGrade) => document.getElementById(`grade-${g}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Hero */}
      <div className="bg-gradient-to-br from-blue-600 to-indigo-700 text-white">
        <div className="max-w-6xl mx-auto px-6 py-10">
          <div className="flex items-center gap-2 text-blue-100 text-sm font-semibold mb-2">
            <GraduationCap size={18} /> Khoá học Vật Lí 365
          </div>
          <h1 className="text-3xl font-black">Khám phá khoá học</h1>
          <p className="text-blue-100 mt-1">Chọn khoá theo lớp và bắt đầu hành trình của bạn.</p>
          {/* Tìm kiếm + chip nhảy nhanh */}
          <div className="mt-5 flex flex-col sm:flex-row gap-3 sm:items-center">
            <div className="relative sm:w-80">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-300" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Tìm khoá học, giáo viên..."
                className="w-full pl-9 pr-3 py-2.5 rounded-xl text-sm text-gray-800 outline-none bg-white/95 focus:ring-2 focus:ring-white/60" />
            </div>
            <div className="flex flex-wrap gap-2">
              {GRADES.filter((g) => byGrade[g].length > 0).map((g) => (
                <button key={g} onClick={() => jump(g)}
                  className="px-3 py-1.5 rounded-full bg-white/15 hover:bg-white/30 text-white text-xs font-bold backdrop-blur">
                  {GRADE_TITLE[g].split(' (')[0]} <span className="opacity-70">({byGrade[g].length})</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-4 md:p-6 space-y-8">
        {loading ? (
          <div className="py-20 text-center text-gray-400">Đang tải khoá học...</div>
        ) : total === 0 ? (
          <div className="py-20 text-center text-gray-400">
            {courses.length === 0 ? 'Chưa có khoá học nào được xuất bản.' : 'Không tìm thấy khoá học phù hợp.'}
          </div>
        ) : (
          GRADES.filter((g) => byGrade[g].length > 0).map((g) => (
            <section key={g} id={`grade-${g}`} className="scroll-mt-20">
              <div className="flex items-center gap-3 mb-4">
                <span className={`w-1.5 h-7 rounded-full bg-gradient-to-b ${GRADE_ACCENT[g]}`} />
                <h2 className="text-xl font-black text-gray-900">{GRADE_TITLE[g]}</h2>
                <span className="text-xs font-bold text-gray-400">{byGrade[g].length} khoá</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {byGrade[g].map((c) => (
                  <CourseCard key={c.id} c={c} accent={GRADE_ACCENT[g]} onClick={() => navigate(`/khoa-hoc/${c.slug}`)} />
                ))}
              </div>
            </section>
          ))
        )}
      </div>
    </div>
  )
}

export default CatalogPage
